import React, { useState, useEffect } from 'react';
import { 
  User, 
  Bell, 
  Shield, 
  Save,
  Eye,
  EyeOff,
  Camera,
  Mail,
  Phone,
  MapPin,
  Check,
  LogOut,
  AlertCircle,
  CreditCard,
  X,
  Calendar,
  DollarSign
} from 'lucide-react';

const Settings = () => {
  const [activeSection, setActiveSection] = useState('profile');
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [error, setError] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isCanceling, setIsCanceling] = useState(false);
  const [showCancelConfirmation, setShowCancelConfirmation] = useState(false);

  // Profile Settings State
  const [profileData, setProfileData] = useState({
    id: null,
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    notifications: true
  });

  // Security Settings State
  const [securityData, setSecurityData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  // Subscription State
  const [subscriptionData, setSubscriptionData] = useState({
    id: null, // This is now the plan name
    status: null,
    currentPeriodStart: null,
    daysUntilRepay: null,
    priceAmount: null,
    willCancel: false,
    loading: true
  });

  // Load user data on component mount
  useEffect(() => {
    loadUserProfile();
    loadSubscriptionData();
  }, []);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('auth_token');
    return {
      'Content-Type': 'application/json',
      'Authorization': token ? `Bearer ${token}` : ''
    };
  };

  const loadUserProfile = async () => {
    try {
      const response = await fetch(process.env.REACT_APP_API_URL + '/users/profile/current', {
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        const userData = await response.json();
        setProfileData({
          id: userData.id,
          firstName: userData.firstName || '',
          lastName: userData.lastName || '',
          email: userData.email || '',
          phone: userData.phone || '',
          address: userData.address || '',
          notifications: userData.notifications !== undefined ? userData.notifications : true
        });
      } else {
        // Fallback to localStorage if API fails
        const userData = JSON.parse(localStorage.getItem('user_data') || '{}');
        setProfileData({
          id: userData.id,
          firstName: userData.firstName || '',
          lastName: userData.lastName || '',
          email: userData.email || '',
          phone: userData.phone || '',
          address: userData.address || '',
          notifications: userData.notifications !== undefined ? userData.notifications : true
        });
      }
    } catch (error) {
      console.error('Failed to load user profile:', error);
      setError('Failed to load user profile');
    }
  };

  // Updated loadSubscriptionData function with better date handling
  const loadSubscriptionData = async () => {
    try {
      const response = await fetch(process.env.REACT_APP_API_URL + '/users/profile/subscription', {
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        const subscription = await response.json();
        
        if (typeof subscription === 'string') {
          // No subscription found
          setSubscriptionData({
            id: null,
            status: null,
            currentPeriodStart: null,
            daysUntilRepay: null,
            priceAmount: null,
            willCancel: false,
            loading: false
          });
        } else {
          // Subscription exists - handle dates more carefully
          let startDate = null;
          if (subscription.currentPeriodStart) {
            // Backend sends Unix timestamp, convert to Date
            startDate = new Date(subscription.currentPeriodStart * 1000);
          }

          setSubscriptionData({
            id: subscription.id, // This is the plan name (Verve Starter/Pro)
            status: subscription.status,
            currentPeriodStart: startDate,
            daysUntilRepay: subscription.daysUntilRepay,
            priceAmount: subscription.priceAmount,
            willCancel: subscription.willCancel || false,
            loading: false
          });
        }
      } else {
        setSubscriptionData(prev => ({ ...prev, loading: false }));
      }
    } catch (error) {
      console.error('Failed to load subscription data:', error);
      setSubscriptionData(prev => ({ ...prev, loading: false }));
    }
  };

  const cancelSubscription = async () => {
    setIsCanceling(true);
    setError('');
    
    try {
      const response = await fetch(process.env.REACT_APP_API_URL + '/users/profile/cancel', {
        method: 'GET',
        headers: getAuthHeaders()
      });

      if (response.ok) {
        const result = await response.json();
        if (result === true) {
          // Successfully canceled - reload subscription data to reflect changes
          await loadSubscriptionData();
          setSaveSuccess(true);
          setTimeout(() => setSaveSuccess(false), 3000);
          setShowCancelConfirmation(false);
        } else {
          setError('Failed to cancel subscription - please try again');
        }
      } else if (response.status === 401) {
        setError('Authentication required - please log in again');
      } else if (response.status === 404) {
        setError('No active subscription found to cancel');
      } else if (response.status === 502) {
        setError('Payment service temporarily unavailable - please try again later');
      } else {
        setError('Failed to cancel subscription - please try again');
      }
    } catch (error) {
      console.error('Cancel subscription failed:', error);
      setError('Network error - please check your connection and try again');
    } finally {
      setIsCanceling(false);
    }
  };

  const settingsSections = [
    { id: 'profile', label: 'Profile', icon: <User className="w-5 h-5" /> },
    { id: 'subscription', label: 'Subscription', icon: <CreditCard className="w-5 h-5" /> },
    { id: 'security', label: 'Security', icon: <Shield className="w-5 h-5" /> },
    { id: 'logout', label: 'Logout', icon: <LogOut className="w-5 h-5" /> }
  ];

  const handleSave = async () => {
    if (!profileData.id) {
      setError('User ID not found');
      return;
    }

    setIsSaving(true);
    setError('');
    
    try {
      const response = await fetch(process.env.REACT_APP_API_URL + `/users/profile/${profileData.id}`, {
        method: 'PATCH',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          firstName: profileData.firstName,
          lastName: profileData.lastName,
          phone: profileData.phone,
          address: profileData.address,
          notifications: profileData.notifications
        })
      });

      if (response.ok) {
        const updatedUser = await response.json();
        // Update localStorage as backup
        localStorage.setItem('user_data', JSON.stringify(updatedUser));
        setSaveSuccess(true);
        setTimeout(() => setSaveSuccess(false), 3000);
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to save profile');
      }
    } catch (error) {
      console.error('Save failed:', error);
      setError('Failed to save profile');
    } finally {
      setIsSaving(false);
    }
  };

  const handleProfileChange = (field, value) => {
    setProfileData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSecurityChange = (field, value) => {
    setSecurityData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleLogout = async () => {
    try {
      // You might want to call a logout endpoint here
      // await fetch(`${API_BASE_URL}/auth/logout`, { method: 'POST', headers: getAuthHeaders() });
      
      // Clear authentication data
      localStorage.removeItem('auth_token');
      localStorage.removeItem('user_data');
      
      // Redirect to login page or trigger parent logout
      window.location.href = '/login'; // or use your routing solution
    } catch (error) {
      console.error('Logout failed:', error);
      // Clear local storage anyway
      localStorage.removeItem('auth_token');
      localStorage.removeItem('user_data');
      window.location.href = '/login';
    }
  };

  const handlePasswordChange = async () => {
    if (!profileData.id) {
      setError('User ID not found');
      return;
    }

    if (securityData.newPassword !== securityData.confirmPassword) {
      setError('New passwords do not match');
      return;
    }

    if (securityData.newPassword.length < 6) {
      setError('New password must be at least 6 characters long');
      return;
    }
    
    setIsSaving(true);
    setError('');
    
    try {
      const response = await fetch(process.env.REACT_APP_API_URL + `/users/password/${profileData.id}`, {
        method: 'PATCH',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          currentPassword: securityData.currentPassword,
          newPassword: securityData.newPassword
        })
      });

      const responseData = await response.json();

      if (response.ok) {
        setSecurityData({
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        });
        setSaveSuccess(true);
        setTimeout(() => setSaveSuccess(false), 3000);
      } else {
        setError(responseData.error || 'Failed to change password');
      }
    } catch (error) {
      console.error('Password change failed:', error);
      setError('Failed to change password');
    } finally {
      setIsSaving(false);
    }
  };

  // Helper function to render timing information more safely
  const renderTimingInfo = () => {
    // If we have a valid daysUntilRepay from backend, use it
    if (subscriptionData.daysUntilRepay !== null && subscriptionData.daysUntilRepay >= 0) {
      const days = subscriptionData.daysUntilRepay;
      
      return (
        <div className={`p-4 border rounded-lg ${
          subscriptionData.willCancel 
            ? 'bg-orange-50 border-orange-200' 
            : days <= 7 
              ? 'bg-yellow-50 border-yellow-200' 
              : 'bg-green-50 border-green-200'
        }`}>
          <div className="flex items-center mb-2">
            <Calendar className={`w-5 h-5 mr-2 ${
              subscriptionData.willCancel 
                ? 'text-orange-600' 
                : days <= 7 
                  ? 'text-yellow-600' 
                  : 'text-green-600'
            }`} />
            <h4 className={`font-medium ${
              subscriptionData.willCancel 
                ? 'text-orange-800' 
                : days <= 7 
                  ? 'text-yellow-800' 
                  : 'text-green-800'
            }`}>
              {subscriptionData.willCancel ? 
                (subscriptionData.status === 'trialing' ? 'Days Until Trial Ends' : 'Days Until Cancellation') :
                (subscriptionData.status === 'trialing' ? 'Days Left in Trial' : 'Days Until Next Payment')
              }
            </h4>
          </div>
          <p className={`text-sm font-semibold ${
            subscriptionData.willCancel 
              ? 'text-orange-700' 
              : days <= 7 
                ? 'text-yellow-700' 
                : 'text-green-700'
          }`}>
            {days === 0 
              ? subscriptionData.willCancel 
                ? (subscriptionData.status === 'trialing' ? 'Trial ends today' : 'Expires today')
                : (subscriptionData.status === 'trialing' ? 'Trial ends today' : 'Payment due today')
              : `${days} ${days === 1 ? 'day' : 'days'} remaining`
            }
          </p>
          {getTimingSubtext(days)}
        </div>
      );
    }

    // Fallback: Show generic message when timing data is unreliable
    return (
      <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <div className="flex items-center mb-2">
          <Calendar className="w-5 h-5 text-blue-600 mr-2" />
          <h4 className="font-medium text-blue-800">
            {subscriptionData.status === 'trialing' ? 'Trial Period' : 'Subscription Active'}
          </h4>
        </div>
        <p className="text-sm text-blue-700">
          {subscriptionData.willCancel ? (
            subscriptionData.status === 'trialing' ? 
              'Your trial is scheduled for cancellation - no payment will be charged.' :
              'Your subscription is scheduled for cancellation at the end of the current period.'
          ) : (
            subscriptionData.status === 'trialing' ? 
              'Your trial is active and will convert to a paid subscription when it ends.' :
              'Your subscription is active and will automatically renew.'
          )}
        </p>
      </div>
    );
  };

  const getTimingSubtext = (days) => {
    if (!subscriptionData.willCancel && subscriptionData.status === 'trialing') {
      return (
        <p className="text-xs text-blue-600 mt-1">
          Your trial will convert to a paid subscription
        </p>
      );
    }
    
    if (!subscriptionData.willCancel && subscriptionData.status === 'active' && days <= 7) {
      return (
        <p className="text-xs text-orange-600 mt-1">
          Payment due soon
        </p>
      );
    }
    
    if (subscriptionData.willCancel) {
      return (
        <p className="text-xs text-orange-600 mt-1">
          {subscriptionData.status === 'trialing' ? 
            'Trial will be canceled - no charge will occur' :
            'Subscription will end after this period'
          }
        </p>
      );
    }
    
    return null;
  };

  // Updated renderSubscriptionSection with improved date handling
  const renderSubscriptionSection = () => (
    <div className="space-y-6">
      <div className="bg-white p-6 rounded-lg shadow">
        <h3 className="text-lg font-semibold mb-4">Subscription Management</h3>
        
        {subscriptionData.loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <span className="ml-3 text-gray-600">Loading subscription details...</span>
          </div>
        ) : subscriptionData.status ? (
          <div className="space-y-6">
            {/* Current Plan and Status */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center mb-2">
                  <DollarSign className="w-5 h-5 text-green-600 mr-2" />
                  <h4 className="font-medium">Current Plan</h4>
                </div>
                <p className="text-sm font-semibold text-gray-800">{subscriptionData.id}</p>
                {subscriptionData.priceAmount && (
                  <p className="text-sm text-gray-600">
                    ${subscriptionData.priceAmount.toFixed(2)}/month
                  </p>
                )}
              </div>

              <div className="p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center mb-2">
                  <CreditCard className="w-5 h-5 text-blue-600 mr-2" />
                  <h4 className="font-medium">Status</h4>
                </div>
                <p className={`text-sm font-semibold ${
                  subscriptionData.status === 'active' ? 'text-green-600' : 
                  subscriptionData.status === 'trialing' ? 'text-blue-600' :
                  subscriptionData.status === 'canceled' ? 'text-red-600' : 'text-yellow-600'
                }`}>
                  {subscriptionData.status === 'trialing' ? 'Trial Period' :
                   subscriptionData.status.charAt(0).toUpperCase() + subscriptionData.status.slice(1)}
                </p>
                {subscriptionData.willCancel && (
                  <p className="text-sm text-orange-600 mt-1">
                    {subscriptionData.status === 'trialing' ? 
                      'Trial will be canceled' : 
                      'Scheduled for cancellation'
                    }
                  </p>
                )}
              </div>
            </div>

            {/* Billing Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {subscriptionData.currentPeriodStart && (
                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-center mb-2">
                    <Calendar className="w-5 h-5 text-blue-600 mr-2" />
                    <h4 className="font-medium text-blue-800">
                      {subscriptionData.status === 'trialing' ? 'Trial Started' : 'Subscription Started'}
                    </h4>
                  </div>
                  <p className="text-sm text-blue-700">
                    {subscriptionData.currentPeriodStart.toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </p>
                </div>
              )}

              {/* Use the improved timing display */}
              {renderTimingInfo()}
            </div>

            {/* Cancel Subscription Section */}
            {(subscriptionData.status === 'active' || subscriptionData.status === 'trialing') && !subscriptionData.willCancel && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-center mb-3">
                  <X className="w-5 h-5 text-red-600 mr-2" />
                  <h4 className="font-medium text-red-800">
                    {subscriptionData.status === 'trialing' ? 'Cancel Trial' : 'Cancel Subscription'}
                  </h4>
                </div>
                <p className="text-sm text-red-700 mb-4">
                  {subscriptionData.status === 'trialing' ? (
                    'Canceling your trial will prevent any charges and end your access when the trial period expires. You won\'t be charged anything.'
                  ) : (
                    'Canceling your subscription will disable auto-renewal. You\'ll retain access until the end of your current billing period.'
                  )}
                </p>
                <button
                  onClick={() => setShowCancelConfirmation(true)}
                  className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
                >
                  {subscriptionData.status === 'trialing' ? 'Cancel Trial' : 'Cancel Subscription'}
                </button>
              </div>
            )}

            {/* Already Canceled Notice */}
            {subscriptionData.willCancel && (
              <div className="p-4 bg-orange-50 border border-orange-200 rounded-lg">
                <div className="flex items-center mb-2">
                  <AlertCircle className="w-5 h-5 text-orange-600 mr-2" />
                  <h4 className="font-medium text-orange-800">
                    {subscriptionData.status === 'trialing' ? 
                      'Trial Scheduled for Cancellation' : 
                      'Subscription Scheduled for Cancellation'
                    }
                  </h4>
                </div>
                <p className="text-sm text-orange-700">
                  {subscriptionData.status === 'trialing' ? 
                    'Your trial has been canceled and you won\'t be charged. You\'ll continue to have access until your trial period ends.' :
                    'Your subscription has been canceled and will not renew. You\'ll continue to have access until your current period ends.'
                  }
                </p>
              </div>
            )}

            {/* Inactive Subscription Notice */}
            {subscriptionData.status === 'canceled' && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-center mb-2">
                  <AlertCircle className="w-5 h-5 text-red-600 mr-2" />
                  <h4 className="font-medium text-red-800">Subscription Ended</h4>
                </div>
                <p className="text-sm text-red-700">
                  Your subscription has ended and you no longer have access to premium features.
                </p>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-8">
            <CreditCard className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h4 className="text-lg font-medium text-gray-600 mb-2">No Active Subscription</h4>
            <p className="text-sm text-gray-500">
              You don't have an active subscription at the moment.
            </p>
          </div>
        )}
      </div>

      {/* Cancel Confirmation Modal */}
      {showCancelConfirmation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg max-w-md w-full mx-4">
            <div className="flex items-center mb-4">
              <AlertCircle className="w-6 h-6 text-red-600 mr-3" />
              <h3 className="text-lg font-semibold">
                {subscriptionData.status === 'trialing' ? 'Confirm Trial Cancellation' : 'Confirm Cancellation'}
              </h3>
            </div>
            
            <p className="text-gray-600 mb-6">
              {subscriptionData.status === 'trialing' ? 
                'Are you sure you want to cancel your trial? You won\'t be charged anything, but you\'ll lose access when the trial ends.' :
                'Are you sure you want to cancel your subscription? This action cannot be undone, but you\'ll retain access until the end of your current billing period.'
              }
            </p>
            
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowCancelConfirmation(false)}
                disabled={isCanceling}
                className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
              >
                {subscriptionData.status === 'trialing' ? 'Keep Trial' : 'Keep Subscription'}
              </button>
              <button
                onClick={cancelSubscription}
                disabled={isCanceling}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 flex items-center"
              >
                {isCanceling ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Canceling...
                  </>
                ) : (
                  subscriptionData.status === 'trialing' ? 'Cancel Trial' : 'Cancel Subscription'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const renderLogoutSection = () => (
    <div className="space-y-6">
      <div className="bg-white p-6 rounded-lg shadow">
        <h3 className="text-lg font-semibold mb-4">Account Actions</h3>
        
        <div className="space-y-4">
          <div className="p-4 bg-red-50 border border-red-200 rounded-md">
            <div className="flex items-center mb-3">
              <LogOut className="w-5 h-5 text-red-600 mr-2" />
              <h4 className="font-medium text-red-800">Logout</h4>
            </div>
            <p className="text-sm text-red-700 mb-4">
              You will be signed out of your account and redirected to the login page.
            </p>
            <button
              onClick={handleLogout}
              className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
            >
              Logout Now
            </button>
          </div>

          <div className="p-4 bg-gray-50 border border-gray-200 rounded-md">
            <div className="flex items-center mb-3">
              <User className="w-5 h-5 text-gray-600 mr-2" />
              <h4 className="font-medium text-gray-800">Session Information</h4>
            </div>
            <div className="text-sm text-gray-600 space-y-1">
              <p><span className="font-medium">Email:</span> {profileData.email}</p>
              <p><span className="font-medium">Last Login:</span> {new Date().toLocaleDateString()}</p>
              <p><span className="font-medium">Session Active:</span> Yes</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderProfileSection = () => (
    <div className="space-y-6">
      <div className="bg-white p-6 rounded-lg shadow">
        <h3 className="text-lg font-semibold mb-4">Personal Information</h3>
        
        <div className="flex items-center mb-6">
          <div className="w-20 h-20 bg-gray-200 rounded-full flex items-center justify-center mr-4">
            <User className="w-8 h-8 text-gray-400" />
          </div>
          <button className="flex items-center px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50">
            <Camera className="w-4 h-4 mr-2" />
            Change Photo
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">First Name</label>
            <input
              type="text"
              value={profileData.firstName}
              onChange={(e) => handleProfileChange('address', e.target.value)}
              className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
      </div>

      <div className="bg-white p-6 rounded-lg shadow">
        <h3 className="text-lg font-semibold mb-4">Notifications</h3>
        
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <Bell className="w-5 h-5 text-gray-400 mr-3" />
            <div>
              <p className="font-medium">Email Notifications</p>
              <p className="text-sm text-gray-500">Receive notifications via email</p>
            </div>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={profileData.notifications}
              onChange={(e) => handleProfileChange('notifications', e.target.checked)}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
          </label>
        </div>
      </div>
    </div>
  );

  const renderSecuritySection = () => (
    <div className="space-y-6">
      <div className="bg-white p-6 rounded-lg shadow">
        <h3 className="text-lg font-semibold mb-4">Change Password</h3>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Current Password</label>
            <div className="relative">
              <input
                type={showCurrentPassword ? "text" : "password"}
                value={securityData.currentPassword}
                onChange={(e) => handleSecurityChange('currentPassword', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter current password"
              />
              <button
                type="button"
                onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                className="absolute right-3 top-2.5"
              >
                {showCurrentPassword ? <EyeOff className="w-5 h-5 text-gray-400" /> : <Eye className="w-5 h-5 text-gray-400" />}
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">New Password</label>
            <div className="relative">
              <input
                type={showNewPassword ? "text" : "password"}
                value={securityData.newPassword}
                onChange={(e) => handleSecurityChange('newPassword', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter new password"
              />
              <button
                type="button"
                onClick={() => setShowNewPassword(!showNewPassword)}
                className="absolute right-3 top-2.5"
              >
                {showNewPassword ? <EyeOff className="w-5 h-5 text-gray-400" /> : <Eye className="w-5 h-5 text-gray-400" />}
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Confirm New Password</label>
            <div className="relative">
              <input
                type={showConfirmPassword ? "text" : "password"}
                value={securityData.confirmPassword}
                onChange={(e) => handleSecurityChange('confirmPassword', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Confirm new password"
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-2.5"
              >
                {showConfirmPassword ? <EyeOff className="w-5 h-5 text-gray-400" /> : <Eye className="w-5 h-5 text-gray-400" />}
              </button>
            </div>
          </div>

          <button
            onClick={handlePasswordChange}
            disabled={isSaving || !securityData.currentPassword || !securityData.newPassword || !securityData.confirmPassword}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSaving ? 'Updating...' : 'Update Password'}
          </button>
        </div>
      </div>
    </div>
  );

  const renderContent = () => {
    switch (activeSection) {
      case 'profile':
        return renderProfileSection();
      case 'subscription':
        return renderSubscriptionSection();
      case 'security':
        return renderSecuritySection();
      case 'logout':
        return renderLogoutSection();
      default:
        return renderProfileSection();
    }
  };

  return (
    <div className="max-w-6xl mx-auto">
      <div className="flex flex-col lg:flex-row gap-6">
        {/* Settings Navigation */}
        <div className="w-full lg:w-64">
          <div className="bg-white rounded-lg shadow p-4">
            <nav className="space-y-2">
              {settingsSections.map((section) => (
                <button
                  key={section.id}
                  onClick={() => setActiveSection(section.id)}
                  className={`w-full flex items-center px-3 py-2 text-left rounded-md transition-colors ${
                    activeSection === section.id
                      ? 'bg-blue-50 text-blue-600 border-r-2 border-blue-600'
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  {section.icon}
                  <span className="ml-3">{section.label}</span>
                </button>
              ))}
            </nav>
          </div>
        </div>

        {/* Settings Content */}
        <div className="flex-1">
          {/* Error Message */}
          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
              <div className="flex items-center">
                <AlertCircle className="w-5 h-5 text-red-600 mr-2" />
                <span className="text-red-700">{error}</span>
              </div>
            </div>
          )}

          {renderContent()}
          
          {/* Save Button - Hide for logout and subscription sections */}
          {activeSection !== 'logout' && activeSection !== 'subscription' && (
            <div className="mt-6 flex justify-end">
              <div className="flex items-center space-x-3">
                {saveSuccess && (
                  <div className="flex items-center text-green-600">
                    <Check className="w-5 h-5 mr-2" />
                    <span>Settings saved successfully!</span>
                  </div>
                )}
                <button
                  onClick={activeSection === 'security' ? handlePasswordChange : handleSave}
                  disabled={isSaving}
                  className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center"
                >
                  {isSaving ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      {activeSection === 'security' ? 'Update Password' : 'Save Changes'}
                    </>
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Settings;